import { SearchBar } from "@/components/SearchBar";
import { useCurrentUser } from "@/hooks/useAuth";
import { useFeatures } from "@/hooks/useFeatures";
import { useUpdateUserSettings } from "@/hooks/useUserData";
import { usePreferences } from "@/store/preferences";
import { withGifsOnly, withoutTypeFilter } from "@/lib/query";

interface Props {
  initialQuery: string;
  onSearch: (query: string) => void;
}

export function ClassicSearch({ initialQuery, onSearch }: Props) {
  const { gifsOnly, setGifsOnly, includeImages, setIncludeImages } = usePreferences();
  const { data: user } = useCurrentUser();
  const { data: features } = useFeatures();
  const updateSettings = useUpdateUserSettings();

  // Effective include-images: the user's column when signed in, the zustand
  // value otherwise. Keeps the checkbox state honest after login.
  const effectiveIncludeImages = user ? user.include_images : includeImages;
  const imagesAvailable = features?.images_indexed === true;

  function handleSubmit(q: string) {
    onSearch(gifsOnly ? withGifsOnly(q) : q);
  }

  function handleGifsOnly(next: boolean) {
    setGifsOnly(next);
    // Re-apply the filter to whatever's currently in the URL so the toggle
    // takes effect immediately, without making the user re-press Search.
    const base = withoutTypeFilter(initialQuery);
    onSearch(next ? withGifsOnly(base) : base);
  }

  function handleIncludeImages(next: boolean) {
    if (user) {
      updateSettings.mutate({ include_images: next });
    } else {
      setIncludeImages(next);
    }
    // The search hook keys on the effective pref, so the open infinite query
    // will refetch automatically — no need to re-submit the query string here.
  }

  return (
    <div className="space-y-2">
      <SearchBar initialQuery={initialQuery} onSearch={handleSubmit} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-400">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={gifsOnly}
            onChange={(e) => handleGifsOnly(e.target.checked)}
            className="accent-accent"
          />
          <span>GIFs only</span>
          <span className="text-zinc-600">— forces <code className="font-mono">type:gif</code></span>
        </label>

        {imagesAvailable && (
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={effectiveIncludeImages}
              onChange={(e) => handleIncludeImages(e.target.checked)}
              className="accent-accent"
              disabled={updateSettings.isPending}
            />
            <span>Include images</span>
            <span className="text-zinc-600">— jpg / png / webp</span>
          </label>
        )}
      </div>
    </div>
  );
}
